import type { LandedTotals, NetVolumeSummary } from '@mentora/data-access'
import type { PriceFormatter } from '../price-formatter'
import { Card, CardContent, CardDescription, CardHeader, CardTitle, cn } from '@mentora/ui-components'
import { useFormatter, useTranslations } from 'next-intl'

// Every line carries the sentence that says how it is computed, so the figure
// never has to be explained out of band.

type Sign = '+' | '−' | '='

function Line({ sign, label, info, amount, note, emphasis }: {
  sign: Sign
  label: string
  info: string
  amount: string
  note?: string
  emphasis?: boolean
}) {
  return (
    <li className={cn('flex flex-col gap-1 py-3', emphasis && 'pt-4')}>
      <div className="flex items-center gap-2">
        <span aria-hidden className="w-4 shrink-0 text-center font-mono text-muted-foreground">{sign}</span>
        <span className={cn('flex-1 text-sm', emphasis ? 'font-semibold text-foreground' : 'text-foreground')}>{label}</span>
        <span className={cn('shrink-0 font-mono tabular-nums', emphasis ? 'text-lg font-bold text-foreground' : 'text-sm text-foreground')}>
          {amount}
        </span>
      </div>
      <p className="pl-6 text-xs leading-relaxed text-muted-foreground">
        {info}
        {note && <span className="mt-1 block text-foreground">{note}</span>}
      </p>
    </li>
  )
}

function LandedBlock({ landed }: { landed: LandedTotals }) {
  const t = useTranslations('dashboard.netVolume')
  const format = useFormatter()
  const money = (cents: number) => format.number(cents / 100, { style: 'currency', currency: landed.currency.toUpperCase() })
  return (
    <div className="rounded-md bg-muted px-3 py-2">
      <p className="text-xs font-medium text-foreground">
        {t('landedTitle', { rail: t('rail'), currency: landed.currency.toUpperCase() })}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
        {t('landedLine', {
          amount: money(landed.amountCents),
          fee: money(landed.feeCents),
          refunded: money(landed.refundedCents),
          net: money(landed.netCents),
        })}
      </p>
    </div>
  )
}

export function NetVolumeCard({ summary, formatPrice }: { summary: NetVolumeSummary, formatPrice: PriceFormatter }) {
  const t = useTranslations('dashboard.netVolume')
  const rail = t('rail')

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <ul className="divide-y divide-border/60">
          <Line sign="+" label={t('gross')} info={t('grossInfo')} amount={formatPrice(summary.grossCents)} note={t('grossHint', { count: summary.salesCount })} />
          <Line
            sign="−"
            label={t('platform')}
            info={t('platformInfo')}
            amount={formatPrice(summary.platformCents)}
            note={summary.platformCreditedCents > 0 ? t('platformCredited', { amount: formatPrice(summary.platformCreditedCents) }) : undefined}
          />
          <Line
            sign="−"
            label={t('fees', { rail })}
            info={t('feesInfo', { rail })}
            amount={formatPrice(summary.feesCents)}
            note={summary.awaitingFeeCount > 0 ? t('awaitingFee', { count: summary.awaitingFeeCount, rail }) : undefined}
          />
          <Line sign="−" label={t('refunds')} info={t('refundsInfo')} amount={formatPrice(summary.refundsCents)} />
          <Line sign="=" label={t('net')} info={t('netInfo')} amount={formatPrice(summary.netCents)} emphasis />
        </ul>
        {summary.landed.map(l => <LandedBlock key={l.currency} landed={l} />)}
      </CardContent>
    </Card>
  )
}
